import React, {useState} from 'react';
import {Alert, Box, MenuItem, Stack, Typography} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import {wrapperSx} from '@/global/helpers/globalstyles';
import {apiCreateTicket} from "@/modules/tickets/services/tickets.service";
import PageHeader from "@/global/components/PageHeader";
import {toast} from "react-toastify";
import {type SubmitHandler, useForm} from "react-hook-form";
import {SelectInputForm} from "@/global/components/FormInputs/SelectInputForm";
import {TextFieldForm} from "@/global/components/FormInputs";
import {LoadingButton} from "@mui/lab";
import type {Accept, FileWithPath} from "react-dropzone";
import {TicketsTypes, zodCreateTicketSchema, type zodCreateTicketType} from "@/modules/tickets";
import {DropZoneBase64} from "@/global/components/DropZoneBase64";
import {convertFileToBase64} from "@/global/helpers/convertFileToBase64";
import {zodResolver} from "@hookform/resolvers/zod";
import {ChangeOldInfo} from "@/modules/tickets/components/ChangeOldInfo";

export function SchoolsNewTicketPage() {

    const navigate = useNavigate();
    const {control, handleSubmit, watch} = useForm<zodCreateTicketType>({
        resolver: zodResolver(zodCreateTicketSchema),
        reValidateMode: 'onChange'
    });

    const watchTitle = watch('title');

    const [files, setFiles] = useState<FileWithPath[]>([]);

    const acceptFileTypes: Accept = {
        'application/pdf': ['.pdf'],
    };

    const {mutate, isPending} = apiCreateTicket();
    const params = useParams();

    const onSubmit: SubmitHandler<zodCreateTicketType> = async (data) => {
        if (!('id' in params) || !params.id) {
            navigate('/schools');
            return;
        }
        const res = await Promise.all(files.map((el) => convertFileToBase64(el)));
        mutate({school_id: +(params.id), ...data, files_url: res}, {
            onSuccess: () => {
                navigate(`/schools/${params.id}/tickets`);
                toast.error('Обращение успешно подано');
            }
        });
    };

    return (
        <Box
            className="App"
            sx={{height: '100%', display: 'flex', flexDirection: 'column'}}
        >
            <PageHeader/>
            <Box sx={wrapperSx}>

                <Stack
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'flex-start'}
                    gap={2}
                >
                    <Typography
                        sx={{mt: 3}}
                        variant={'h5'}
                    >
                        <b>
                            Новое обращение
                        </b>
                    </Typography>
                    <Typography variant="subtitle2">
                        Оставьте обращение, связанное с проблемами или вопросами по системе. Наша техническая поддержка
                        ответит Вам в течение рабочего дня.
                    </Typography>
                    <Alert severity={'info'}>
                        Данное обращение при необходимости будет перенаправлено в государственные органы – владельцам
                        АИС «Автошкола» - КАП МВД РК
                    </Alert>
                </Stack>

                <Box
                    className={'appeals'}
                    sx={{p: '16px', background: '#fff', borderRadius: '16px', mt: 2}}
                >
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        style={{display: 'flex', flexDirection: 'column', gap: '20px', margin: '10px'}}
                    >
                        <SelectInputForm
                            control={control}
                            name={'title'}
                            label={'Тема обращения'}
                            sx={{
                                '&.MuiOutlinedInput-root': {
                                    border: 'none',
                                }
                            }}
                            items={Object.entries(TicketsTypes).map(([title, description]) => (
                                <MenuItem
                                    key={title}
                                    value={title}
                                >
                                    {description}
                                </MenuItem>
                            ))}
                        />
                        {
                            watchTitle === 'archive-table' &&
                            <ChangeOldInfo control={control}/>
                        }
                        <TextFieldForm
                            multiline={true}
                            rows={12}
                            margin={'dense'}
                            control={control}
                            label={'Текст обращения'}
                            name={'message'}
                        />
                        <DropZoneBase64
                            maxSizeMb={1}
                            accept={acceptFileTypes}
                            description={"(Формат: pdf)"}
                            maxFiles={5}
                            onUpload={(files) => setFiles(files)}
                        />
                        <LoadingButton
                            loading={isPending}
                            type={'submit'}
                            variant={'contained'}
                        >
                            Отправить
                        </LoadingButton>
                    </form>
                </Box>
            </Box>
        </Box>
    );
}